import { PurchaseOrderReceptionApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderReceptionApi.ts";
import { PurchaseOrderReceipt } from "src/api/generated/erp/db/types/tables/purchaseOrderReceipt.ts";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPurchaseOrderReceiptStateLabels } from "src/api/generated/erp/db/types/enums/purchaseOrderReceiptState.ts";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { PurchaseOrderReceiptForm } from "src/components/views/erp/purchase/reception/PurchaseOrderReceiptForm.tsx";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import { faCheck, faExternalLinkAlt, faPlus, faPrint, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { PurchaseOrderReceptionView } from "src/components/views/erp/purchase/reception/PurchaseOrderReceptionView.tsx";
import { PurchaseOrder } from "../../../../../api/generated/erp/db/types/tables/purchaseOrder.ts";
import { OpenPurchaseOrderSourcePickingViewButton } from "./OpenPurchaseOrderSourcePickingViewButton.tsx";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PurchaseOrderReceiptsDataGridProps {
	purchaseOrder: PurchaseOrder;
	showPickingButton?: boolean;
	onSelectionChanged?: (selection: PurchaseOrderReceipt | undefined) => void;
	selectFirstRowOnLoad?: boolean;
	editEnabled?: boolean;
	showOpenReceptionButton?: boolean;
	/** Read-only in the purchase order view, which reads receipts with the purchase order read permission. */
	purchaseOrderView?: boolean;
}

export const PurchaseOrderReceiptsDataGrid = ({
	purchaseOrder,
	showPickingButton = false,
	onSelectionChanged,
	selectFirstRowOnLoad = false,
	editEnabled = false,
	showOpenReceptionButton = false,
	purchaseOrderView = false,
}: PurchaseOrderReceiptsDataGridProps) => {
	const { openDialog } = useGenericDialog();

	const { dataGridProps, onlySelectedRow, refreshData } = useClientSideDataGridModel({
		fetchData: () =>
			purchaseOrderView ?
				PurchaseOrderReceptionApi.getPurchaseOrderReceiptsForPurchaseOrderView({
					purchaseOrderId: purchaseOrder.purchaseOrderId,
				})
			:	PurchaseOrderReceptionApi.getPurchaseOrderReceipts({
					purchaseOrderId: purchaseOrder.purchaseOrderId,
				}),
		getRowId: (row) => row.purchaseOrderReceiptId,
		customFunctionUiActionView: "PURCHASE_ORDER_RECEIPTS",
		gridId: "752688618A17A960",
		initialParams: {},
		onSelectionChanged: (selection) => {
			onSelectionChanged?.(selection[0]);
		},
		selectFirstRowOnLoad: selectFirstRowOnLoad,
	});

	return (
		<ControlledAsyncFormCrudDataGrid<PurchaseOrderReceipt>
			columns={[
				enumColumn({
					field: "purchaseOrderReceiptState",
					headerName: i18n.t("state"),
					enumLabels: getPurchaseOrderReceiptStateLabels(),
					width: 120,
				}),
				dateColumn({
					field: "createdDate",
					headerName: i18n.t("created_at"),
					width: 120,
				}),
				textColumn({
					field: "createdBy",
					headerName: i18n.t("created_by"),
					width: 150,
				}),
				dateColumn({
					field: "receiptedDate",
					headerName: i18n.t("receipted_at"),
					width: 120,
				}),
				textColumn({
					field: "note",
					headerName: i18n.t("note"),
					width: 300,
				}),
				integerColumn({
					field: "purchaseOrderReceiptId",
					headerName: i18n.t("id"),
				}),
			]}
			actionBarComponents={
				<>
					{editEnabled && (
						<AsyncButton
							icon={faPlus}
							onClick={async () => {
								await PurchaseOrderReceptionApi.createReceipt({
									purchaseOrderId: purchaseOrder.purchaseOrderId,
									note: "",
								});
								await refreshData();
							}}
						/>
					)}
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef(
							"PURCHASE_ORDER_RECEIPT",
							onlySelectedRow?.purchaseOrderReceiptId,
						)}
					/>
					{showOpenReceptionButton && purchaseOrder.purchaseOrderState !== "INITIAL" && (
						<AsyncButton
							label={i18n.t("open_reception")}
							icon={faExternalLinkAlt}
							variant={"outlined"}
							onClick={() => {
								openDialog({
									title: i18n.t("reception"),
									agentContext: {
										key: defineAgentUiContextKey("erp.purchase.purchaseOrderReception.dialog"),
										title: i18n.t("reception"),
										data: {
											purchaseOrderId: purchaseOrder.purchaseOrderId,
										},
									},
									content: <PurchaseOrderReceptionView purchaseOrder={purchaseOrder} />,
								});
							}}
						/>
					)}
					{editEnabled && onlySelectedRow?.purchaseOrderReceiptState === "INITIAL" && (
						<AsyncButton
							label={i18n.t("mark_as_completed")}
							icon={faCheck}
							variant={"outlined"}
							onClick={async () => {
								await PurchaseOrderReceptionApi.markPurchaseOrderReceiptReceived({
									purchaseOrderReceiptId: onlySelectedRow.purchaseOrderReceiptId,
								});
								await refreshData();
							}}
						/>
					)}
					{editEnabled && onlySelectedRow?.purchaseOrderReceiptState === "RECEIPTED" && (
						<AsyncButton
							label={i18n.t("return_to_handling")}
							icon={faUndo}
							variant={"outlined"}
							onClick={async () => {
								await PurchaseOrderReceptionApi.revertMarkPurchaseOrderReceiptReceived({
									purchaseOrderReceiptId: onlySelectedRow.purchaseOrderReceiptId,
								});
								await refreshData();
							}}
						/>
					)}
					{showPickingButton && <OpenPurchaseOrderSourcePickingViewButton purchaseOrder={purchaseOrder} />}
					<AsyncButton
						label={i18n.t("delivery_notes.printout")}
						icon={faPrint}
						variant={"outlined"}
						disabled={onlySelectedRow == null}
						onClick={async () => {
							if (onlySelectedRow == null) return;
							const purchaseOrderReceiptId = onlySelectedRow.purchaseOrderReceiptId;
							const fileHandle =
								purchaseOrderView ?
									await PurchaseOrderReceptionApi.printPurchaseOrderReceiptDeliveryNotesForPurchaseOrderView(
										{
											purchaseOrderReceiptId,
										},
									)
								:	await PurchaseOrderReceptionApi.printPurchaseOrderReceiptDeliveryNotes({
										purchaseOrderReceiptId,
									});
							downloadFile(fileHandle);
						}}
					/>
				</>
			}
			form={{
				addRowEnabled: false,
				editEnabled: editEnabled,
				dialogTitle: i18n.t("receipt"),
				dialogSize: "sm",
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.purchase.purchaseOrderReceipt.formDialog"),
					title: i18n.t("receipt"),
					data: {
						purchaseOrderId: purchaseOrder.purchaseOrderId,
						purchaseOrderReceiptId: row?.purchaseOrderReceiptId ?? null,
					},
				}),
				component: ({ row, ...other }) => (
					<PurchaseOrderReceiptForm
						purchaseOrderId={purchaseOrder.purchaseOrderId}
						purchaseOrderReceipt={row}
						{...other}
					/>
				),
			}}
			remove={
				editEnabled ?
					{
						type: "enabled",
						action: ({ items }) =>
							PurchaseOrderReceptionApi.deleteReceipts({
								purchaseOrderReceiptIds: items.map((item) => item.purchaseOrderReceiptId),
							}),
					}
				:	{ type: "hidden" }
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.purchase.purchaseOrderReceipts.grid"),
				data: {
					purchaseOrderId: purchaseOrder.purchaseOrderId,
				},
				rowFields: ["purchaseOrderReceiptId", "purchaseOrderId", "purchaseOrderReceiptState"],
				selectedRowPathSegment: (row) => String(row.purchaseOrderReceiptId),
			}}
			{...dataGridProps}
		/>
	);
};
